import { WEEKDAYS, inputClass } from './FormKit';

export default function SlotGenerator({ weeklySlots, config, onConfigChange }) {
  const bulkMode = !!config.bulkMode;
  const bulkDays = config.bulkDays || [];
  const daySlots = weeklySlots[config.day] || [];
  const allSelected = bulkDays.length === WEEKDAYS.length;

  function updateConfig(patch) {
    onConfigChange({ ...config, ...patch });
  }

  function toggleBulkDay(day) {
    const next = bulkDays.includes(day) ? bulkDays.filter(d => d !== day) : [...bulkDays, day];
    updateConfig({ bulkDays: next });
  }

  function toggleAllDays() {
    updateConfig({ bulkDays: allSelected ? [] : [...WEEKDAYS] });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#e8eaed] dark:border-[#3c4043] bg-[#f8fafd] dark:bg-[#202124]/50 px-3.5 py-2.5">
        <div>
          <p className="text-xs font-semibold text-[#202124] dark:text-[#e8eaed]">Bulk generate</p>
          <p className="text-[11px] text-[#5f6368] dark:text-[#9aa0a6]">Apply these settings to multiple weekdays at once.</p>
        </div>
        <button type="button" role="switch" aria-checked={bulkMode}
          onClick={() => updateConfig({ bulkMode: !bulkMode })}
          className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 cursor-pointer"
          style={{ background: bulkMode ? '#1a73e8' : undefined }}
        >
          <span className={`absolute inset-0 rounded-full transition-colors ${bulkMode ? 'bg-[#1a73e8]' : 'bg-[#dadce0] dark:bg-[#3c4043]'}`} />
          <span className={`relative inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${bulkMode ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {!bulkMode ? (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {WEEKDAYS.map(day => (
            <button key={day} type="button" onClick={() => updateConfig({ day })}
              className={`px-3.5 py-2 rounded-xl text-xs font-semibold capitalize shrink-0 whitespace-nowrap transition-colors cursor-pointer ${
                config.day === day
                  ? 'bg-[#1a73e8] text-white'
                  : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#5f6368] dark:text-[#9aa0a6] hover:bg-[#e8eaed] dark:hover:bg-[#303134]'
              }`}>
              {day.slice(0, 3)}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#e8eaed] dark:border-[#3c4043] p-3 space-y-3">
          <label className="flex items-center gap-2 text-xs font-semibold text-[#202124] dark:text-[#e8eaed] cursor-pointer pb-2 border-b border-[#e8eaed] dark:border-[#3c4043]">
            <input type="checkbox" checked={allSelected} onChange={toggleAllDays} className="w-4 h-4 accent-[#1a73e8] rounded" />
            Select all days
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {WEEKDAYS.map(day => (
              <label key={day}
                className={`flex items-center gap-2 text-xs font-semibold capitalize rounded-xl border px-2.5 py-2 cursor-pointer transition-colors ${
                  bulkDays.includes(day)
                    ? 'bg-[#e8f0fe] dark:bg-[#1a2e4c] border-[#c8dcfb] dark:border-[#1a73e8]/30 text-[#1a73e8] dark:text-[#8ab4f8]'
                    : 'bg-white dark:bg-[#303134] border-[#dadce0] dark:border-[#3c4043] text-[#5f6368] dark:text-[#9aa0a6]'
                }`}>
                <input type="checkbox" checked={bulkDays.includes(day)} onChange={() => toggleBulkDay(day)} className="w-4 h-4 accent-[#1a73e8] rounded" />
                {day.slice(0, 3)}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[#e8eaed] dark:border-[#3c4043] bg-[#f8fafd] dark:bg-[#202124]/50 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">Open</label>
          <input type="time" value={config.openTime}
            onChange={e => updateConfig({ openTime: e.target.value })} className={inputClass} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">Close</label>
          <input type="time" value={config.closeTime}
            onChange={e => updateConfig({ closeTime: e.target.value })} className={inputClass} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">Duration (min)</label>
          <input type="number" min="1" value={config.durationMinutes}
            onChange={e => updateConfig({ durationMinutes: e.target.value })} className={inputClass} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">Default Capacity</label>
          <input type="number" min="1" value={config.capacity}
            onChange={e => updateConfig({ capacity: e.target.value })} className={inputClass} />
        </div>
      </div>

      <p className="text-[11px] text-[#70757a] dark:text-[#9aa0a6]">
        {bulkMode
          ? (bulkDays.length > 0
              ? `Saving will replace all existing slots for ${bulkDays.length} selected day${bulkDays.length === 1 ? '' : 's'} with slots generated from these settings.`
              : 'Select at least one day above to generate slots for.')
          : (daySlots.length > 0
              ? `Saving will replace all ${daySlots.length} existing slot(s) for ${config.day} with slots generated from these settings.`
              : `Saving will generate slots for ${config.day} from these settings.`)}
        {' '}Fine-tune individual dates on the Slots page.
      </p>
    </div>
  );
}
